import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { updateProfessional, clearProfessional } from '@/reducers/graph';
import { RootState } from '@/types/graph';
import { Professional } from '@aldebaran/prisma';
import { ProfessionalWithPractice } from '@aldebaran/common/types';
import { QuestionnaireOptions } from '@aldebaran/common/qVariants';
import { usePractice } from '@/hooks/usePractice';
import { useMode } from '@/hooks/useMode';
import { useQuestionnaireNavigation } from '@/context/NavigationProvider';

export const useLandingProfessionalSelect = () => {
  const mode = useMode();
  const dispatch = useDispatch();
  const { navigateNext } = useQuestionnaireNavigation();

  const invitationId = useSelector((state: RootState) => state.invitationId);
  const previousProfessional = useSelector(
    (state: RootState) => state.consultation.professional,
  );
  const [selectedProfessional, setSelectedProfessional] =
    useState<ProfessionalWithPractice | null>(previousProfessional || null);
  const wasManuallyUnselected = useRef(false);
  const consultationDate = useSelector((state: RootState) =>
    state.consultation.date ? new Date(state.consultation.date) : undefined,
  );

  const practiceSlug = useSelector(
    (state: RootState) => state.consultation.practice?.slug,
  );

  const { data: practice } = usePractice(practiceSlug);

  useEffect(() => {
    if (
      !selectedProfessional &&
      previousProfessional &&
      !wasManuallyUnselected.current
    ) {
      setSelectedProfessional(previousProfessional);
    } else if (practice?.users && practice.users.length === 1) {
      setSelectedProfessional({
        ...practice.users[0],
        practice,
        questionnaireOptions: practice.users[0]
          .questionnaireOptions as QuestionnaireOptions | null,
      });
    }
  }, [practice, previousProfessional, selectedProfessional]);

  useEffect(() => {
    if (selectedProfessional) {
      dispatch(updateProfessional(selectedProfessional));
    } else {
      dispatch(clearProfessional());
    }
  }, [selectedProfessional, dispatch]);

  const professional = practice?.users.find(
    (u) => u.id === selectedProfessional?.id,
  );

  const professionals =
    mode === 'SALLE_ATTENTE' || mode === 'PRACTICE_LINK'
      ? practice?.users ?? []
      : professional
      ? [professional]
      : [];

  const isInvitationFlow = mode === 'INVITATION' && !!invitationId;

  const handleNavigateNext = useCallback(() => {
    navigateNext();
  }, [navigateNext]);

  const handleSelectProfessional = useCallback(
    (professional: Professional | null) => {
      if (professional && practice) {
        wasManuallyUnselected.current = false;
        setSelectedProfessional({
          ...professional,
          practice,
          questionnaireOptions:
            professional.questionnaireOptions as QuestionnaireOptions | null,
        });
      } else {
        wasManuallyUnselected.current = true;
        setSelectedProfessional(null);
      }
    },
    [practice],
  );

  return {
    mode,
    professional,
    professionals,
    selectedProfessional,
    practice,
    consultationDate,
    isInvitationFlow,
    handleNavigateNext,
    handleSelectProfessional,
  };
};
